// ============================================================
// sale-content/ContentFormModal.js — ฟอร์มเพิ่ม/แก้เนื้อหา 1 ชิ้น
// ------------------------------------------------------------
// แสดงเฉพาะช่องที่ section นั้นใช้จริงบนเว็บ (ดู fields ใน sections.js)
// ส่ง payload กลับให้หน้าแม่ไปยิง API — ไฟล์นี้ไม่ยิง API เอง
//
// ★ รูป: ย่อด้วย react-image-file-resizer แล้วส่ง base64 ให้ backend อัปขึ้น S3
//   รูปเดิมที่ไม่ได้แตะจะส่ง URL เดิมกลับไป backend คืนค่าเดิม ไม่อัปซ้ำ
// ============================================================

import React, { Component } from "react";
import Resizer from "react-image-file-resizer";
import { Modal } from "../CustomComponent.js";
import { LISTING_TYPES, budgetsFor, parseShortcut, buildShortcut, describeShortcut, normalizeUrl } from "./sections.js";

const resizeToBase64 = (file) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(file, 1600, 1600, "JPEG", 90, 0, (uri) => resolve(uri), "base64");
  });

// ค่าเริ่มต้นของฟอร์มจากแถวเดิม — null จาก API ต้องเป็นสตริงว่าง ไม่งั้น input กลายเป็น uncontrolled
const toForm = (section, item) => {
  const form = {};
  (section?.fields || []).forEach((f) => {
    form[f.key] = item?.[f.key] || "";
  });
  return form;
};

export default class ContentFormModal extends Component {
  state = { form: {}, errors: {}, resizing: false };

  componentDidUpdate(prevProps) {
    // เปิดฟอร์มใหม่ทุกครั้งต้องเริ่มจากข้อมูลของชิ้นที่กดแก้ ไม่ใช่ค่าที่ค้างจากครั้งก่อน
    if (this.props.show && (!prevProps.show || prevProps.item !== this.props.item)) {
      this.setState({ form: toForm(this.props.section, this.props.item), errors: {}, resizing: false });
    }
  }

  setField = (key, value) =>
    this.setState((prev) => ({ form: { ...prev.form, [key]: value }, errors: { ...prev.errors, [key]: "" } }));

  pickImage = async (key, event) => {
    const file = (event.target.files || [])[0];
    event.target.value = "";
    if (!file) return;
    this.setState({ resizing: true });
    const base64 = await resizeToBase64(file);
    this.setState({ resizing: false });
    this.setField(key, base64);
  };

  validate = () => {
    const { section } = this.props;
    const { form } = this.state;
    const errors = {};
    (section.fields || []).forEach((f) => {
      const value = String(form[f.key] || "").trim();
      if (f.required && !value) errors[f.key] = `กรุณากรอก${f.label}`;
      if (f.maxLength && value.length > f.maxLength) errors[f.key] = `ยาวได้ไม่เกิน ${f.maxLength} ตัวอักษร`;
    });
    if (section.atLeastOne && section.atLeastOne.every((k) => !String(form[k] || "").trim())) {
      errors[section.atLeastOne[0]] = "กรอกอย่างน้อย 1 ช่อง (ถ้าไม่ต้องการข้อความเอง ให้ลบชิ้นนี้แทน หน้าเว็บจะใช้ข้อความมาตรฐาน)";
    }
    this.setState({ errors });
    return !Object.keys(errors).length;
  };

  submit = () => {
    if (this.state.resizing || !this.validate()) return;
    const { section } = this.props;
    const { form } = this.state;
    const payload = {};
    (section.fields || []).forEach((f) => {
      let value = typeof form[f.key] === "string" ? form[f.key].trim() : form[f.key];
      if (f.type === "url") value = normalizeUrl(value);
      // ว่าง = null ให้ backend ล้างค่าเดิมทิ้ง (รูปว่าง backend จะตั้ง image_url = null ให้)
      payload[f.key] = value || null;
    });
    this.props.onSubmit(payload);
  };

  // ---------- ช่องแต่ละแบบ ----------

  renderIcon = (f) => {
    const value = this.state.form[f.key] || "";
    const known = f.icons.some((i) => i.code === value);
    return (
      <div className="sc-icon-grid">
        {!f.required && (
          <button
            type="button"
            className={`sc-icon-btn sc-icon-btn--wide ${!value ? "is-active" : ""}`}
            onClick={() => this.setField(f.key, "")}
          >
            <span className="text-12">{f.emptyLabel || "ไม่ใช้ไอคอน"}</span>
          </button>
        )}
        {/* ไอคอนเดิมที่ไม่อยู่ในชุด (ใส่ผ่าน API มาก่อน) — ยังเลือกคงไว้ได้ ไม่ถูกล้างทิ้งเงียบ ๆ */}
        {!!value && !known && (
          <button type="button" className="sc-icon-btn is-active" title="ไอคอนเดิม">
            <span className="icon sc-glyph">{value}</span>
            <small>ไอคอนเดิม</small>
          </button>
        )}
        {f.icons.map((i) => (
          <button
            key={i.code}
            type="button"
            title={i.label}
            className={`sc-icon-btn ${value === i.code ? "is-active" : ""}`}
            onClick={() => this.setField(f.key, i.code)}
          >
            <span className="icon sc-glyph">{i.code}</span>
            <small>{i.label}</small>
          </button>
        ))}
      </div>
    );
  };

  renderImage = (f) => {
    const value = this.state.form[f.key] || "";
    const inputId = `sc-image-${f.key}`;
    return (
      <div>
        <input hidden accept="image/*" id={inputId} type="file" onChange={(e) => this.pickImage(f.key, e)} />
        {value ? (
          <div className="sc-image-preview">
            <img src={value} alt={f.label} />
            <div className="d-flex gap-2 mt-2">
              <button type="button" className="prop-btn prop-btn--ghost prop-btn--sm" onClick={() => document.getElementById(inputId).click()}>
                เปลี่ยนภาพ
              </button>
              <button type="button" className="prop-btn prop-btn--danger prop-btn--sm" onClick={() => this.setField(f.key, "")}>
                ลบภาพ
              </button>
            </div>
          </div>
        ) : (
          <div
            className="border border-2 border-dash rounded-4 d-flex align-items-center justify-content-center pointer py-4"
            style={{ background: "#f6f8fa" }}
            onClick={() => document.getElementById(inputId).click()}
          >
            <div className="text-center text-secondary">
              <div className="icon text-32">{""}</div>
              <small>{this.state.resizing ? "กำลังย่อรูป..." : "เลือกภาพ"}</small>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ตัวช่วยสร้างเงื่อนไขทางลัด — เก็บเป็น query string ที่หน้าเว็บอ่านได้ (ดู shortcut() ใน home/index.js)
  renderShortcut = (f) => {
    const filters = parseShortcut(this.state.form[f.key]);
    const update = (key, value) => {
      const next = { ...filters, [key]: value };
      // เปลี่ยนประเภทประกาศแล้วช่วงราคาเดิมคนละหลัก (ขาย=ล้าน / เช่า=พัน) ต้องล้างทิ้ง เหมือนหน้าเว็บ
      if (key === "listing_type") next.budget = "";
      this.setField(f.key, buildShortcut(next));
    };
    return (
      <div className="sc-shortcut">
        <div className="row g-3">
          <div className="col-12 col-md-4">
            <label className="prop-label">ประเภทประกาศ</label>
            <select className="prop-field" value={filters.listing_type} onChange={(e) => update("listing_type", e.target.value)}>
              {LISTING_TYPES.map((i) => (
                <option key={i.value} value={i.value}>{i.label}</option>
              ))}
            </select>
          </div>
          <div className="col-12 col-md-4">
            <label className="prop-label">ช่วงราคา</label>
            <select className="prop-field" value={filters.budget} onChange={(e) => update("budget", e.target.value)}>
              {budgetsFor(filters.listing_type).map((i) => (
                <option key={i.value} value={i.value}>{i.label}</option>
              ))}
              {/* ค่าเดิมที่ไม่อยู่ในชุด — แสดงไว้ให้เห็น ไม่ล้างทิ้งเอง */}
              {!!filters.budget && !budgetsFor(filters.listing_type).some((i) => i.value === filters.budget) && (
                <option value={filters.budget}>ค่าเดิม {filters.budget}</option>
              )}
            </select>
            <div className="pf-hint">{filters.listing_type === "เช่า" ? "ค่าเช่าต่อเดือน" : "ราคาขาย"}</div>
          </div>
          <div className="col-12 col-md-4">
            <label className="prop-label">คำค้นเลขห้อง</label>
            <input
              className="prop-field"
              placeholder="เช่น 101/1 (ไม่ใส่ก็ได้)"
              value={filters.data_search}
              onChange={(e) => update("data_search", e.target.value)}
            />
          </div>
        </div>
        <div className="pf-hint mt-2">
          กดปุ่มนี้บนเว็บแล้วจะค้นหา: <b>{describeShortcut(this.state.form[f.key])}</b>
          {" · "}ตัวกรองโครงการที่ลูกค้าเลือกไว้จะคงเดิม
        </div>
      </div>
    );
  };

  renderField = (f) => {
    const { form, errors } = this.state;
    let input;
    if (f.type === "icon") input = this.renderIcon(f);
    else if (f.type === "image") input = this.renderImage(f);
    else if (f.type === "shortcut") input = this.renderShortcut(f);
    else if (f.type === "textarea") {
      input = (
        <textarea
          className="prop-field"
          rows={f.rows || 3}
          placeholder={f.placeholder || ""}
          value={form[f.key] || ""}
          onChange={(e) => this.setField(f.key, e.target.value)}
        />
      );
    } else {
      input = (
        <input
          className="prop-field"
          type="text"
          inputMode={f.type === "url" ? "url" : undefined}
          maxLength={f.maxLength}
          placeholder={f.placeholder || ""}
          value={form[f.key] || ""}
          onChange={(e) => this.setField(f.key, e.target.value)}
        />
      );
    }

    return (
      <div className="pf-section mb-3" key={f.key}>
        <label className="prop-label">
          {f.label}
          {f.required && <span className="text-danger"> *</span>}
        </label>
        {input}
        {!!f.hint && <div className="pf-hint">{f.hint}</div>}
        {!!errors[f.key] && <div className="text-danger text-12 mt-1">{errors[f.key]}</div>}
      </div>
    );
  };

  render() {
    const { show, section, item, saving, onHide, projectLabel } = this.props;
    if (!section) return null;
    return (
      <Modal show={show} size="lg" centered backdrop="static" onHide={onHide}>
        <Modal.Header>
          <Modal.Title>
            <b>{item ? "แก้ไข" : "เพิ่ม"}{section.label}</b>
            <div className="text-12 text-secondary fw-normal mt-1">{projectLabel}</div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="pf-hint mb-3">แสดงที่: {section.where}</div>
          {(section.fields || []).map(this.renderField)}
          {!!section.note && <div className="pf-hint">หมายเหตุ: {section.note}</div>}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="prop-btn prop-btn--ghost" disabled={saving} onClick={onHide}>
            ยกเลิก
          </button>
          <button type="button" className="prop-btn" disabled={saving || this.state.resizing} onClick={this.submit}>
            {saving ? "กำลังบันทึก..." : "บันทึก"}
          </button>
        </Modal.Footer>
      </Modal>
    );
  }
}
